/**
 * Per-tenant calendar business hours (Jun 2026).
 * Stored on the tenant's organization record as 24h "HH:MM" strings
 * (business_hours_start / business_hours_end). Unset → legacy default
 * 08:00–23:00 grid and NO after-hours warning.
 */
export const DEFAULT_BUSINESS_HOURS = { start: '08:00', end: '23:00' };

export function toMinutes(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

export function getBusinessHours(organizations) {
  const org = Array.isArray(organizations) ? organizations[0] : organizations;
  const start = org?.business_hours_start;
  const end = org?.business_hours_end;
  const configured = !!(start && end) && toMinutes(end) > toMinutes(start);
  if (!configured) return { ...DEFAULT_BUSINESS_HOURS, configured: false };
  return { start, end, configured: true };
}

/** True when the appointment's time-of-day falls outside configured hours.
 *  Never warns when the tenant hasn't configured business hours. */
export function isOutsideBusinessHours(startVal, endVal, bh) {
  if (!bh?.configured) return false;
  const s = startVal instanceof Date ? startVal : new Date(startVal);
  if (!startVal || Number.isNaN(s.getTime())) return false;
  const bhStart = toMinutes(bh.start);
  const bhEnd = toMinutes(bh.end);
  const startMin = s.getHours() * 60 + s.getMinutes();
  if (startMin < bhStart || startMin >= bhEnd) return true;
  if (endVal) {
    const e = endVal instanceof Date ? endVal : new Date(endVal);
    if (!Number.isNaN(e.getTime()) && e > s) {
      let endMin = e.getHours() * 60 + e.getMinutes();
      if (endMin === 0) endMin = 24 * 60; // ends exactly at midnight
      if (endMin > bhEnd) return true;
    }
  }
  return false;
}

export function formatHHMM12(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h)) return hhmm || '';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
}
